import { useTranslation } from 'react-i18next'
import i18n from '@/utils/i18n'
import clsx from 'clsx'
import {
    deriveLeadStage,
  leadPipelineUi,
  leadStageOrder,
  leadStatusLabelKey,
  type LeadPipeline,
  type InspectionLeadRow,
  type InspectionLeadStage,
} from '@/types/inspection-lead'
import { LeadKanbanCard } from '@/components/inspections/LeadKanbanCard'

/**
 * Канбан-доска лидов: колонка на каждый стейдж воронки (LEAD_STAGE_ORDER),
 * карточки внутри отсортированы как пришли из API (created_at desc).
 *
 * Стейдж — синтетика, не raw lead.status: добавлена колонка «Успешно» для
 * (paid + связанный заказ COMPLETED). Маппинг см. в `deriveLeadStage`.
 *
 * Внешний вид синхронизирован с канбаном заказов (`DashboardPage`): колонки
 * `flex-1` во всю высоту с независимым внутренним скроллом, фон `surface`,
 * заголовок Space Grotesk, оранжевый бейдж-счётчик. Доска визуальная:
 * перетаскивания нет, статус меняется на детальной карточке лида.
 */
export function LeadKanbanBoard({
  rows,
  pipeline = 'avito',
  stageCounts,
  onLoadMore,
  loadingMore,
}: {
  rows: InspectionLeadRow[]
  /** Воронка: определяет набор колонок (у СберАвто свои этапы). */
  pipeline?: LeadPipeline
  /** Реальные счётчики стадий по всей базе (для бейджей). Если не переданы —
   *  считаем по загруженным карточкам (старое поведение). */
  stageCounts?: Partial<Record<InspectionLeadStage, number>>
  /** Подгрузить следующую порцию лидов (расширить окно выборки). */
  onLoadMore?: () => void
  /** Идёт подгрузка — блокируем кнопку «Показать ещё». */
  loadingMore?: boolean
}) {
  const { t } = useTranslation()
  const stages = leadStageOrder(pipeline)
  const idleStages = leadPipelineUi(pipeline).idleStages
  const byStage = new Map<InspectionLeadStage, InspectionLeadRow[]>()
  for (const s of stages) byStage.set(s, [])
  for (const r of rows) {
    const stage = deriveLeadStage(r.status, r.order_status)
    byStage.get(stage)?.push(r)
  }

  return (
    <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2">
      {stages.map((stage) => {
        const items = byStage.get(stage) ?? []
        // Бейдж — реальный счётчик по всей базе; если его нет, fallback на
        // число загруженных карточек. «не загружено» — сколько карточек этой
        // стадии не влезло в окно выборки (терминальные стадии — старые лиды).
        const total = stageCounts?.[stage] ?? items.length
        const notLoaded = Math.max(0, total - items.length)
        // Этап без действий с нашей стороны: ход у Carlink / СберАвто / клиента
        // либо этап конечный. Бирюзовая точка перед названием с подсказкой,
        // название переносится на вторую строку, а не обрезается. Фон не красим.
        const idle = idleStages.includes(stage)
        return (
          <div
            key={stage}
            className="flex min-h-[min(100%,calc(100dvh-12rem))] min-w-[240px] flex-1 flex-col gap-2 rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-2"
          >
            <div className="flex shrink-0 items-start justify-between gap-2 px-1 pt-1">
              <span
                title={idle ? t('leads.kanban.idleHint') : undefined}
                className={clsx(
                  'min-w-0 font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold leading-tight tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]',
                )}
              >
                {idle ? (
                  // Точка в потоке текста: по вертикали на середине первой
                  // строки заголовка, при переносе остаётся у первой строки.
                  // Сдвиг через vertical-align, не transform: transform уводит
                  // точку на отдельный слой, и при горизонтальной прокрутке
                  // доски она отстаёт от заголовка, а потом «перелетает».
                  <span
                    aria-label={t('leads.kanban.idle')}
                    className="mr-2 inline-block h-2 w-2 rounded-full bg-[color:var(--stryzh-color-accent-carlink)] [vertical-align:0.1em]"
                  />
                ) : null}
                {t(leadStatusLabelKey('stage', stage, pipeline, (k) => i18n.exists(k)))}
              </span>
              <span className="inline-flex min-h-[1.5rem] min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-2 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
                {total}
              </span>
            </div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pl-1 pr-3 pb-1 pt-[var(--stryzh-space-2)] [scrollbar-gutter:stable]">
              {items.length === 0 ? (
                <div className="py-8 text-center text-xs text-[var(--stryzh-color-text-secondary)]">
                  {t('common.noData')}
                </div>
              ) : (
                items.map((lead) => <LeadKanbanCard key={lead.id} lead={lead} />)
              )}
              {notLoaded > 0 ? (
                onLoadMore ? (
                  <button
                    type="button"
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    className="w-full rounded-md py-2 text-center text-[10px] text-[color:var(--stryzh-color-accent-orange)] hover:bg-[rgba(255,255,255,0.04)] disabled:opacity-50"
                  >
                    {loadingMore ? t('common.loading') : t('leads.kanban.loadMore', { n: notLoaded })}
                  </button>
                ) : (
                  <div className="py-2 text-center text-[10px] text-[var(--stryzh-color-text-secondary)]">
                    {t('leads.kanban.notLoaded', { n: notLoaded })}
                  </div>
                )
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}
